// @flow

import { useEffect, useState } from 'react';
import { authPkgConfig } from '@auravisionlabs/aura-pkg-auth-service';
import {
  getAuthPkgUser,
  initAuthPkgUser,
} from '../../services/auth-pkg-user';

export type UserProfilePermissionsT = {
  loading: boolean,
  canReadUserProfile: boolean,
  canCreateUserProfile: boolean,
  canUpdateUserProfile: boolean,
  canDeleteUserProfile: boolean,
  // Composed conveniences — encode the §10·3 / §7 rule ("add affordances
  // need both create AND update") in one place so consumers don't have
  // to recompute it on every render.
  canAddPreference: boolean,
  canBulkAdd: boolean,
  canBulkRemove: boolean,
};

const DENY: UserProfilePermissionsT = {
  loading: false,
  canReadUserProfile: false,
  canCreateUserProfile: false,
  canUpdateUserProfile: false,
  canDeleteUserProfile: false,
  canAddPreference: false,
  canBulkAdd: false,
  canBulkRemove: false,
};

export const useUserProfilePermissions = (): UserProfilePermissionsT => {
  const [perms, setPerms] = useState<UserProfilePermissionsT>({
    ...DENY,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let authUser = getAuthPkgUser();
        if (!authUser) {
          authUser = await initAuthPkgUser();
        }
        if (!authUser) {
          if (mounted) setPerms({ ...DENY, loading: false });
          return;
        }
        const orgId = await authUser.getPrimaryOrgId();
        const R = authPkgConfig.resources;
        const A = authPkgConfig.authActions;

        const [
          canRead,
          canCreate,
          canUpdate,
          canDelete,
        ] = await Promise.all([
          authUser.can(R.userProfile, A.read, { orgIds: [orgId] }).evaluate(),
          authUser.can(R.userProfile, A.create, { orgIds: [orgId] }).evaluate(),
          authUser.can(R.userProfile, A.update, { orgIds: [orgId] }).evaluate(),
          authUser.can(R.userProfile, A.delete, { orgIds: [orgId] }).evaluate(),
        ]);

        if (!mounted) return;
        const canAdd = !!(canCreate && canUpdate);
        setPerms({
          loading: false,
          canReadUserProfile: !!canRead,
          canCreateUserProfile: !!canCreate,
          canUpdateUserProfile: !!canUpdate,
          canDeleteUserProfile: !!canDelete,
          canAddPreference: canAdd,
          canBulkAdd: canAdd,
          canBulkRemove: !!canDelete,
        });
      } catch (e) {
        if (mounted) setPerms({ ...DENY, loading: false });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return perms;
};
